/*
 * Access gate + single-contour session login. Runs at app startup (imported
 * first in index.ts), BEFORE the Telegram UI, no heavy deps.
 *
 * Flow:
 *  - `?slug=&pw=` in URL  -> store creds, strip URL.
 *  - no creds             -> block with a "get a link" screen.
 *  - creds but no session -> auth, fetch /session/inject, write tweb's
 *                            `account1` (auth_key), reload -> tweb boots into
 *                            the account (manager never sees phone/code).
 *  - session present       -> start presence + a revoke-worker that wipes the
 *                            session and returns to the gate once revoked.
 */

const API_BASE: string =
  (import.meta as any).env?.VITE_CONTROL_API || 'http://localhost:8000';

const TOKEN_KEY = 'control_token';
const SLUG_KEY = 'control_slug';
const PW_KEY = 'control_password';
const ACCOUNT_KEY = 'account1'; // tweb's localStorage auth for account #1

function overlay(title: string, subtitle: string) {
  const mount = () => {
    let el = document.getElementById('control-access-block') as HTMLDivElement;
    if(!el) {
      el = document.createElement('div');
      el.id = 'control-access-block';
      el.style.cssText = 'position:fixed;inset:0;z-index:2147483647;display:flex;' +
        'align-items:center;justify-content:center;background:#17212b;color:#fff;' +
        'font-family:sans-serif;text-align:center;padding:24px';
      document.body.appendChild(el);
    }
    el.innerHTML = `<div><div style="font-size:56px;margin-bottom:16px">🔒</div>` +
      `<div style="font-size:20px;font-weight:600;margin-bottom:8px">${title}</div>` +
      `<div style="opacity:.7">${subtitle}</div></div>`;
  };
  if(document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount);
}

function showAccessBlock() {
  overlay('Доступ ограничен', 'Получите ссылку доступа у администратора.');
}

function hasCreds() {
  return !!(localStorage.getItem(SLUG_KEY) && localStorage.getItem(PW_KEY));
}

async function authToken(): Promise<string | null> {
  const slug = localStorage.getItem(SLUG_KEY);
  const pw = localStorage.getItem(PW_KEY);
  if(!slug || !pw) return null;
  const r = await fetch(`${API_BASE}/auth/${slug}`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({password: pw})
  });
  if(!r.ok) return null;
  const data = await r.json();
  localStorage.setItem(TOKEN_KEY, data.token);
  return data.token;
}

// Fetch the account auth and write tweb's `account1`, then reload so tweb boots
// logged in. Returns false if it couldn't (blocked).
async function injectSession(): Promise<void> {
  overlay('Входим в аккаунт…', 'Подождите пару секунд.');
  const token = await authToken();
  if(!token) {
    showAccessBlock();
    return;
  }
  const r = await fetch(`${API_BASE}/session/inject`, {
    headers: {Authorization: `Bearer ${token}`}
  });
  if(!r.ok) {
    showAccessBlock();
    return;
  }
  const {dc_id, auth_key_hex, user_id} = await r.json();
  const account: Record<string, any> = {
    [`dc${dc_id}_auth_key`]: auth_key_hex,
    auth_key_fingerprint: auth_key_hex.slice(0, 8),
    userId: user_id,
    dcId: dc_id,
    // Pin the base DC hard: tweb otherwise migrates to App.baseDcId (=2) on a
    // transient boot 401 and strands the session on a DC it isn't logged into.
    controlDcId: dc_id,
    date: Math.floor(Date.now() / 1000)
  };
  localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
  // Pin the home DC in a standalone key tweb never rewrites (account1.dcId gets
  // stripped during boot re-auth). apiManager.getBaseDcId reads this first.
  // JSON-encoded to match LocalStorageController's get() (JSON.parse).
  localStorage.setItem('control_dc', JSON.stringify(dc_id));
  location.reload();
}

// Poll /session/status; on revoke wipe the session + creds and return to gate.
function startRevokeWorker() {
  const tick = async() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if(!token) return;
    try {
      const r = await fetch(`${API_BASE}/session/status`, {
        headers: {Authorization: `Bearer ${token}`}
      });
      if(r.status === 401) return; // token expired; presence/next inject re-auths
      if(!r.ok) return;
      const {revoked} = await r.json();
      if(revoked) {
        [ACCOUNT_KEY, TOKEN_KEY, SLUG_KEY, PW_KEY, 'control_dc']
        .forEach((k) => localStorage.removeItem(k));
        location.reload();
      }
    } catch(e) {
      /* best-effort */
    }
  };
  tick();
  setInterval(tick, 30_000);
}

(function controlGate() {
  try {
    const url = new URL(location.href);
    const slug = url.searchParams.get('slug');
    const pw = url.searchParams.get('pw');
    if(slug && pw) {
      localStorage.setItem(SLUG_KEY, slug);
      localStorage.setItem(PW_KEY, pw);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ACCOUNT_KEY); // force a fresh session injection
      url.searchParams.delete('slug');
      url.searchParams.delete('pw');
      history.replaceState(null, '', url.toString());
    }

    (window as any).controlLogin = (s: string, p: string) => {
      localStorage.setItem(SLUG_KEY, s);
      localStorage.setItem(PW_KEY, p);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ACCOUNT_KEY);
      location.reload();
    };

    if(!hasCreds()) {
      showAccessBlock();
      return;
    }

    if(!localStorage.getItem(ACCOUNT_KEY)) {
      injectSession(); // async -> writes account1 -> reload
      return;
    }

    // Session present: heartbeat + revoke-worker, then let tweb boot.
    import('@lib/control/apiClient').then((m) => m.startControlPresence());
    import('@lib/control/checkSound').then((m) => m.initCheckSound());
    import('@lib/control/cacheBust').then((m) => m.startVersionWatch());
    startRevokeWorker();
  } catch(e) {
    /* no window/localStorage — ignore */
  }
})();
